import { useMemo } from 'react'
import { trackEvent } from '@retailer-platform/dashboard/analytics'
import { useRetailerId } from '@retailer-platform/dashboard/routing'

export type EventKey = 'id' | 'description'
export type StoreHourEvent = Record<EventKey, string>
export type StoreHourEventMetadata = {
  retailerId: string
  sortBy?: string
  filterBy?: string
}

export const EVENT_TRACKING = {
  loadListPage: {
    id: 'store_hours_bulk.list.page_load',
    description: 'The store hours /list page is loaded',
  } as StoreHourEvent,
  loadUpdateHistoryPage: {
    id: 'store_hours_bulk.update_history.page_load',
    description: 'The Store Hours / Update History page is loaded',
  } as StoreHourEvent,
  loadHolidaysListPage: {
    id: 'store_hours_bulk.holidays.list.page_load',
    description: 'The store hours /holiday/list page is loaded',
  } as StoreHourEvent,
  loadHolidayStoreListPage: {
    id: 'store_hours_bulk.holidays.holiday.stores.list.page_load',
    description: 'The store hours /holiday/list/:date/stores-confirmed page is loaded',
  } as StoreHourEvent,
  singleStoreEditClicked: {
    id: 'store_hours_bulk.edit.single_edit_clicked',
    description: 'The button to edit a single store is clicked',
  } as StoreHourEvent,
  bulkStoreEditClicked: {
    id: 'store_hours_bulk.edit.bulk_edit_clicked',
    description: 'The button to edit many stores is clicked',
  } as StoreHourEvent,
  storeHourUpdateSubmitted: {
    id: 'store_hours_bulk.edit.update_submitted',
    description: 'A request submitted to update store hours',
  } as StoreHourEvent,
  storeHourUpdateSuccessful: {
    id: 'store_hours_bulk.edit.update_successful',
    description: 'A request submitted to update store hours was successful',
  } as StoreHourEvent,
  storeHourUpdateFailure: {
    id: 'store_hours_bulk.edit.update_failure',
    description: 'A request submitted to update store hours failed',
  } as StoreHourEvent,
  storeHourFilterBySearchText: {
    id: 'store_hours_bulk.list.filter.search_text',
    description: 'User filtered Store Hours by search text field',
  } as StoreHourEvent,
  storeHourFilterByStoreGroup: {
    id: 'store_hours_bulk.list.filter.store_group',
    description: 'User filtered Store Hours by Store Group',
  } as StoreHourEvent,
  storeHourFilterByState: {
    id: 'store_hours_bulk.list.filter.state',
    description: 'User filtered Stores Hours by State',
  } as StoreHourEvent,
  storeHourFilterByCity: {
    id: 'store_hours_bulk.list.filter.city',
    description: 'User filtered Store Hours list by City',
  } as StoreHourEvent,
  storeHourFilterByLaunchStatus: {
    id: 'store_hours_bulk.list.filter.launchStatus',
    description: 'User filtered Store Hours list by store launch status',
  } as StoreHourEvent,
  storeHourFilterByConfirmationStatus: {
    id: 'store_hours_bulk.list.filter.confirmationStatus',
    description: "User filtered Store Hours list by store's hour confirmation status",
  } as StoreHourEvent,
  storeHourChangeSortOrder: {
    id: 'store_hours_bulk.list.sort',
    description: 'User changed sort order of Store Hours list',
  } as StoreHourEvent,
  storeHourConfirmationUpdateSubmitted: {
    id: 'store_hours_bulk.edit.confirmation_update_submitted',
    description: 'A request submitted to confirm store hours',
  } as StoreHourEvent,
  storeHourConfirmationUpdateSuccessful: {
    id: 'store_hours_bulk.edit.confirmation_update_successful',
    description: 'A request submitted to confirm store hours was successful',
  } as StoreHourEvent,
  storeHourConfirmationUpdateFailure: {
    id: 'store_hours_bulk.edit.confirmation_update_failure',
    description: 'A request submitted to confirm store hours failed',
  } as StoreHourEvent,
}

/**
 * Used with `trackStoreHoursEvent` for pulling the metadata which uses React hooks out.
 */
// disabled for now, may use in the future
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const useTrackStoreEventMetaData = (event: StoreHourEvent) => {
  const retailerId = useRetailerId()

  return useMemo(() => ({ retailerId } as StoreHourEventMetadata), [retailerId])
}

/**
 * Tracks an event without the use of any hooks. Useful for when wanting to track
 * an event while NOT rendering a React component.
 *
 * See `useTrackStoreEventMetaData`
 */
export const trackStoreHoursEvent = (
  event: StoreHourEvent,
  metadata: StoreHourEventMetadata,
  numberOfStores?: number
) => {
  trackEvent({
    ...event,
    data: { ...metadata, numberOfStores },
  })
}

/**
 * The simplest way to track an event. As with all React hooks, must be used when rendering
 * a React component.
 *
 * If you want to track an event outside of a React component rendering, see `trackStoreHoursEvent`.
 */
export const useTrackStoreHoursEvent = (event: StoreHourEvent, numberOfStores?: number) => {
  const metadata = useTrackStoreEventMetaData(event)
  trackStoreHoursEvent(event, metadata, numberOfStores)
}
